
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { enrollMFA, verifyMFA } from '@/utils/authUtils';

interface MFASetupProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onSuccess, onCancel }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [setupData, setSetupData] = useState<{ 
    factorId: string;
    qrCode: string;
    secret: string;
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { toast } = useToast();

  // Fetch enrollment QR code on component mount
  useEffect(() => {
    async function setupMFA() {
      try {
        const data = await enrollMFA();
        setSetupData(data);
      } catch (error) {
        console.error('Error setting up MFA:', error);
        toast({
          title: 'Error',
          description: 'Failed to set up MFA. Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    }
    
    setupMFA();
  }, [toast]);

  // Verify MFA setup
  const handleVerify = async () => {
    if (!setupData || verificationCode.length < 6) return;
    
    setIsVerifying(true);
    try {
      await verifyMFA(setupData.factorId, verificationCode);
      toast({
        title: 'Success',
        description: 'Two-factor authentication has been enabled for your account.',
      });
      onSuccess();
    } catch (error) {
      console.error('Error verifying MFA setup:', error);
      toast({
        title: 'Verification Failed',
        description: 'The code you entered is incorrect. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto bg-tokscript-card border-gray-800">
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-tokscript-cta" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto bg-tokscript-card border-gray-800">
      <CardHeader>
        <CardTitle className="text-xl text-center">Set Up Two-Factor Authentication</CardTitle>
        <CardDescription className="text-center">
          Enhance your account security by enabling two-factor authentication
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {setupData && (
          <>
            <div className="flex justify-center py-2">
              <img 
                src={setupData.qrCode} 
                alt="QR Code for authenticator app" 
                className="w-48 h-48 border-4 border-white p-2 bg-white rounded-md"
              />
            </div>
            
            <Alert className="bg-gray-800 border-gray-700">
              <AlertDescription className="text-sm">
                Scan the QR code with your authenticator app, or enter this setup key manually: <code className="bg-gray-900 px-1 py-0.5 rounded">{setupData.secret}</code>
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2 pt-4">
              <h4 className="text-sm font-medium text-center">Enter the 6-digit code from your authenticator app</h4>
              <div className="flex justify-center py-2">
                <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="bg-gray-800 border-gray-700" />
                    <InputOTPSlot index={1} className="bg-gray-800 border-gray-700" />
                    <InputOTPSlot index={2} className="bg-gray-800 border-gray-700" />
                    <InputOTPSlot index={3} className="bg-gray-800 border-gray-700" />
                    <InputOTPSlot index={4} className="bg-gray-800 border-gray-700" />
                    <InputOTPSlot index={5} className="bg-gray-800 border-gray-700" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={isVerifying}
          className="border-gray-700 hover:bg-gray-800"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleVerify} 
          disabled={verificationCode.length < 6 || isVerifying}
          className="bg-tokscript-cta hover:bg-tokscript-cta/90 text-black"
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying
            </>
          ) : (
            'Verify and Enable'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default MFASetup;
